import './DatePickerSingleGroup.css'
import { DayPicker, Matcher } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import { isValid, Locale, parse } from 'date-fns'
import { useEffect, useState } from 'react'
import { CalendarIcon } from '../icons'
import { Input } from '../Input/Input'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { format } from 'date-fns'
import { Label } from '../InputLabel/InputLabel'
import { getLocaleDateFormat } from '../../utils/datePickerHelpers'

export type DatePickerSingleCommonProps = {
  locale: Locale
  date?: Date
  disabled?: Matcher | Matcher[] | undefined
  startMonth?: Date
  endMonth?: Date
  onChange: (date: Date | null) => void
}

function DatePickerSingle({
  date,
  disabled,
  onChange,
  startMonth,
  endMonth,
  locale
}: DatePickerSingleCommonProps) {
  const [month, setMonth] = useState<Date | null>(date || null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(date || null)

  const handleDayPickerSelect = (date?: Date) => {
    if (!date) {
      setSelectedDate(null)
      onChange(null)
      return
    }

    setSelectedDate(date)
    onChange(date)
  }

  useEffect(() => {
    if (!date) {
      setSelectedDate(null)
      setMonth(null)
    }
  }, [date])

  return (
    <>
      <DayPicker
        mode="single"
        required
        captionLayout="dropdown"
        onMonthChange={setMonth}
        selected={selectedDate || undefined}
        startMonth={startMonth}
        endMonth={endMonth}
        onSelect={handleDayPickerSelect}
        month={month || undefined}
        locale={locale}
        disabled={disabled}
      />
    </>
  )
}

export function DatePickerSingleGroup({
  id,
  date,
  disabled,
  onChange,
  startMonth,
  endMonth,
  locale,
  placeholder,
  disabledInput,
  label,
  tooltip,
  tooltipPlacement,
  tooltipSize,
  required
}: DatePickerSingleCommonProps & {
  id: string
  label?: string
  placeholder?: string
  disabledInput?: boolean
  tooltip?: React.ReactNode
  tooltipPlacement?: 'top' | 'bottom'
  tooltipSize?: 'small' | 'medium' | 'large'
  required?: boolean
}) {
  const inputDateFormat = getLocaleDateFormat(locale)
  const inputDateFormatLength = inputDateFormat.length
  const [inputValue, setInputValue] = useState(date ? `${format(date, inputDateFormat)}` : null)

  useEffect(() => {
    setInputValue(date ? `${format(date, inputDateFormat)}` : null)
  }, [date, inputDateFormat])

  const handleChange = (updatedDate: Date | null) => {
    setInputValue(updatedDate ? `${format(updatedDate, inputDateFormat)}` : null)

    onChange(updatedDate)
  }

  const handleInputChange = (value: string) => {
    setInputValue(value)
    const date = parse(value, inputDateFormat, new Date())
    if (isValid(date) && value.length === inputDateFormatLength) {
      onChange(date)
    } else {
      onChange(null)
    }
  }

  useEffect(() => {
    if (!date) {
      setInputValue(null)
      return
    }
  }, [date])

  return (
    <div>
      {label && (
        <Label
          className="date-picker-group-label"
          sizeType={'sm'}
          htmlFor={id}
          tooltip={tooltip}
          tooltipPlacement={tooltipPlacement}
          tooltipSize={tooltipSize}
          required={required}
        >
          {label}
        </Label>
      )}
      <div className="date-picker-input-container">
        <Input
          id={id}
          type="text"
          disabled={disabledInput}
          maxLength={inputDateFormatLength}
          value={inputValue || ''}
          readOnly
          placeholder={placeholder || inputDateFormat}
          onChange={evt => handleInputChange(evt.target.value)}
          className="date-picker-input-full"
        />
        <Popover className="date-picker-popover">
          {({ close }) => (
            <>
              <PopoverButton disabled={disabledInput}>
                <CalendarIcon className="date-picker-calendar-icon" />
              </PopoverButton>
              <PopoverPanel anchor="bottom" transition className="date-picker-panel">
                <DatePickerSingle
                  date={date}
                  locale={locale}
                  disabled={disabled}
                  startMonth={startMonth}
                  endMonth={endMonth}
                  onChange={d => {
                    handleChange(d)
                    close()
                  }}
                />
              </PopoverPanel>
            </>
          )}
        </Popover>
      </div>
    </div>
  )
}
